import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { useBetslip } from '@/betslip';
import { formatOddForDisplay } from '@/domain/odds-format';
import { formatAmount } from '@/domain/player-balance';
import { cn } from '@/lib/cn';

/** Match legacy `BetPopup` auto-hide (classic themes). */
const AUTO_HIDE_MS = 10_000;

/** Routes where legacy never shows the betpopup mini bar. */
export function isBetslipPopupBlockedPath(pathname: string): boolean {
  if (pathname === '/betslip' || pathname.startsWith('/betslip/')) return true;
  if (pathname.startsWith('/casino') || pathname.startsWith('/livecasino')) return true;
  if (pathname.startsWith('/profile')) return true;
  if (pathname.startsWith('/login') || pathname.startsWith('/register')) return true;
  return false;
}

/**
 * Legacy mobile `betpopup`: summary above the bottom nav after a selection is added.
 * Format: `{stake} @ {odds} Returns: {possibleWin}`. Tap opens the betslip.
 */
export function MobileBetslipPopup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user } = useAuthSession();
  const { selectionCount, stake, maxOdds, possibleWin } = useBetslip();
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const blocked = isBetslipPopupBlockedPath(pathname);
  const currency = (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode)?.trim() || '';

  useEffect(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (selectionCount <= 0 || blocked) {
      setVisible(false);
      return;
    }

    setVisible(true);
    hideTimerRef.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS);

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [selectionCount, blocked]);

  if (!visible || selectionCount <= 0 || blocked) return null;

  const stakeLabel = currency ? `${currency}${formatAmount(stake)}` : formatAmount(stake);
  const oddsLabel = formatOddForDisplay(maxOdds);
  const returnsLabel = currency
    ? `${currency}${formatAmount(possibleWin)}`
    : formatAmount(possibleWin);

  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/betslip', search: {} })}
      aria-label={t('OpenBetslip', {
        defaultValue: 'Open bet slip, {{stake}} at {{odds}}, returns {{returns}}',
        stake: stakeLabel,
        odds: oddsLabel,
        returns: returnsLabel,
      })}
      className={cn(
        'mx-5 mb-2 w-[calc(100%-2.5rem)] rounded-md bg-status-warning px-3 py-3',
        'text-center text-[13px] font-bold leading-4 text-bg-canvas shadow-sm',
      )}
    >
      <span>{stakeLabel}</span>
      <span className="mx-1">@</span>
      <span>{oddsLabel}</span>
      <span className="ml-2">{t('Returns', { defaultValue: 'returns' })}:</span>
      <span className="ml-1">{returnsLabel}</span>
    </button>
  );
}
